import type { Entity, Page, Query, Repository, Unsubscribe } from '@buildmate/core';
import { ConflictError, NotFoundError, stripUndefined } from '@buildmate/core';
import { applyOrderBy, matchesWhere } from './query-engine.ts';

/**
 * Deep clone on the way in and out.
 *
 * Without this a caller holding a returned object could mutate the store by
 * accident, which the real database would never allow — and the fake would
 * quietly diverge from production behaviour.
 */
function clone<T>(value: T): T {
  return structuredClone(value);
}

/**
 * Written values are normalised exactly as the Firestore adapter normalises
 * them, so an unset optional field behaves identically in both. Skipping this
 * is how a fake drifts from the real database.
 */
function normalise<T>(value: T): T {
  return stripUndefined(clone(value));
}

export class MemoryRepository<T extends Entity> implements Repository<T> {
  private readonly rows = new Map<string, T>();
  private readonly listeners = new Set<() => void>();

  private readonly collectionName: string;

  constructor(collectionName = 'memory') {
    this.collectionName = collectionName;
  }

  async get(id: string): Promise<T | null> {
    const row = this.rows.get(id);
    return row ? clone(row) : null;
  }

  async list(query?: Query): Promise<Page<T>> {
    return this.listSync(query);
  }

  private listSync(query?: Query): Page<T> {
    let items = [...this.rows.values()];

    for (const clause of query?.where ?? []) {
      items = items.filter((item) => matchesWhere(item, clause));
    }
    items = applyOrderBy(items, query?.orderBy);

    if (query?.cursor) {
      const index = items.findIndex((item) => item.id === query.cursor);
      items = index === -1 ? [] : items.slice(index + 1);
    }

    if (query?.limit !== undefined && items.length > query.limit) {
      const pageItems = items.slice(0, query.limit);
      return { items: pageItems.map(clone), nextCursor: pageItems.at(-1)?.id };
    }

    return { items: items.map(clone) };
  }

  async create(entity: T): Promise<void> {
    if (this.rows.has(entity.id)) throw new ConflictError(this.collectionName, entity.id);
    this.rows.set(entity.id, normalise(entity));
    this.notify();
  }

  async update(id: string, patch: Partial<Omit<T, 'id'>>): Promise<void> {
    const existing = this.rows.get(id);
    if (!existing) throw new NotFoundError(this.collectionName, id);
    // A patch key set to undefined clears the field, matching Firestore's
    // deleteField() semantics rather than being ignored.
    const merged = { ...existing, ...patch, id } as T;
    this.rows.set(id, normalise(merged));
    this.notify();
  }

  async delete(id: string): Promise<void> {
    // Deleting an absent row is a no-op, matching the real database rather
    // than forcing every caller to check first.
    if (this.rows.delete(id)) this.notify();
  }

  /**
   * Test hook: make the next subscription fail.
   *
   * Lets a test exercise the error path — a denied Firestore listener — which
   * is otherwise only reachable against a real backend with restrictive rules.
   */
  failNextSubscribe(error: Error): void {
    this.pendingFailure = error;
  }

  private pendingFailure: Error | null = null;

  subscribe(
    query: Query | undefined,
    onChange: (page: Page<T>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (this.pendingFailure) {
      const failure = this.pendingFailure;
      this.pendingFailure = null;
      onError?.(failure);
      return () => {};
    }
    const emit = () => onChange(this.listSync(query));
    this.listeners.add(emit);
    emit();
    return () => {
      this.listeners.delete(emit);
    };
  }

  subscribeOne(
    id: string,
    onChange: (entity: T | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (this.pendingFailure) {
      const failure = this.pendingFailure;
      this.pendingFailure = null;
      onError?.(failure);
      return () => {};
    }
    const emit = () => {
      const row = this.rows.get(id);
      onChange(row ? clone(row) : null);
    };
    this.listeners.add(emit);
    emit();
    return () => {
      this.listeners.delete(emit);
    };
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }

  /** Test helper: wipe the collection. */
  clear(): void {
    this.rows.clear();
    this.notify();
  }

  get size(): number {
    return this.rows.size;
  }
}
